"use client";

import React from "react";
import { Award, Wrench, ShieldCheck, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const proSpecialties = [{
  id: 1,
  title: "Refrigeration Specialists",
  focus: "Sealed systems, compressors, evaporators, and ice makers",
  description: "Pros focused on domestic refrigerators and freezers who troubleshoot temperature swings, defrost failures, and coolant cycle faults.",
  badge: "Kitchen Refrigeration",
  link: "/RefrigeratorRepairPage",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/expert_michael_chen.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/expert_michael_chen.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/expert_michael_chen.png-wvc-sizes",
  imageAlt: "Technician evaluating mechanical components on a residential appliance"
}, {
  id: 2,
  title: "Laundry Appliance Pros",
  focus: "Washers, electric dryers, gas dryers, and stacked laundry",
  description: "Local technicians skilled in resolving spin cycle halts, drainage pump clogs, drive belt replacements, and heating element burnouts.",
  badge: "Washers & Dryers",
  link: "/WasherRepairPage",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/expert_sarah_jenkins.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/expert_sarah_jenkins.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/expert_sarah_jenkins.png-wvc-sizes",
  imageAlt: "Appliance specialist performing electrical multimeter diagnostic check"
}, {
  id: 3,
  title: "Cooking & Range Technicians",
  focus: "Gas burners, electric cooktops, igniters, and bake elements",
  description: "Contractors experienced with electric and gas kitchen ranges, wall ovens, cooktops, and touch control boards.",
  badge: "Ovens & Ranges",
  link: "/OvenAndStoveRepairPage",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/expert_david_rodriguez.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/expert_david_rodriguez.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/expert_david_rodriguez.png-wvc-sizes",
  imageAlt: "Technician repairing home appliance interior wiring with specialized tools"
}, {
  id: 4,
  title: "Dishwasher & Disposal Pros",
  focus: "Water valves, circulation pumps, spray arms, and motor impellers",
  description: "Independent pros troubleshooting wash quality issues, door latch leaks, food disposal blockages, and under-sink plumbing connections.",
  badge: "Dishwashers & Kitchen Cleaners",
  link: "/DishwasherRepairPage",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/expert_james_wilson.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/expert_james_wilson.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/expert_james_wilson.png-wvc-sizes",
  imageAlt: "Service technician troubleshooting appliance components inside a home"
}];
export default function ApplianceRepairExpertProfiles() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="appliancerepairexpertprofiles" className="bg-background py-16 md:py-24 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div ref={headerRef} className={`flex flex-col md:flex-row justify-between items-start md:items-end mb-12 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="max-w-2xl">
            <h2 className="text-3xl md:text-4xl font-default font-bold text-foreground mb-4">
              Types of Local Pros You Can Be Matched With
            </h2>
            <p className="text-muted-foreground text-lg">
              Our marketplace connects Atlanta homeowners with independent trade professionals specializing across key appliance categories.
            </p>
          </div>
          <Button asChild variant="outline" className="mt-6 md:mt-0 shrink-0 group">
            <Link to="#bookingrequest">
              Request a Match
              <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Button>
        </div>

        <div ref={gridRef} className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 transition-all duration-1000 ease-out delay-200 ${isGridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {proSpecialties.map((pro, i) => <Card key={pro.id} data-index={i} className="flex flex-col overflow-hidden border-border/50 shadow-sm hover:shadow-md transition-shadow duration-300">
              <AspectRatio ratio={4 / 3} className="bg-muted">
                <img src={pro.image} data-wvc-srcset={pro.imageSrcset} data-wvc-sizes={pro.imageSizes} alt={pro.imageAlt} className="object-cover w-full h-full" loading="lazy" />
              </AspectRatio>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-default">{pro.title}</CardTitle>
                <CardDescription className="text-xs font-medium text-primary">
                  {pro.focus}
                </CardDescription>
              </CardHeader>
              <CardContent className="flex-grow flex flex-col gap-3">
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {pro.description}
                </p>
                <div className="mt-auto pt-2">
                  <Badge variant="secondary" className="font-normal text-xs bg-secondary/10 text-secondary-foreground">
                    <Award className="w-3 h-3 mr-1.5 text-primary" />
                    {pro.badge}
                  </Badge>
                </div>
              </CardContent>
              <CardFooter className="pt-0">
                <Button asChild variant="outline" className="w-full">
                  <Link to={pro.link}>View Service Details</Link>
                </Button>
              </CardFooter>
            </Card>)}
        </div>
      </div>
    </section>;
}